import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import { AttachmentIcon, DeleteIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useDeleteIncidentReportAttachmentMutation } from "@/services/reports";
import { useSystem } from "@/utils/hooks/useLocation";

const ALLOWED_ATTACHMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];
const allowedExtension = (name) => /\.(pdf|jpe?g|png)$/i.test(name || "");
const isAllowedFile = (file) =>
  ALLOWED_ATTACHMENT_TYPES.includes(file?.type) || allowedExtension(file?.name);

/** When doc is a URL string, use it as href and filename from path; when object, use file_url/url and name fields */
const getDocumentDisplay = (doc) => {
  if (typeof doc === "string") {
    return { url: doc, name: doc.split("/").pop() || "Document" };
  }
  const url = doc?.file_url ?? doc?.url ?? doc?.file_path ?? doc?.path ?? null;
  const name = doc?.original_name || doc?.file_name || doc?.name || (url ? url.split("/").pop() : null) || "Document";
  return { url, name };
};

function DocumentField({ control, healthCertificateInputRef, existingDocuments = [], reportId = null }) {
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const [attachmentError, setAttachmentError] = useState("");
  const [deletingIndex, setDeletingIndex] = useState(null);
  const deleteAttachment = useDeleteIncidentReportAttachmentMutation({ system: currentSystem });

  return (
    <Controller
      name="documents"
      control={control}
      render={({ field: { value, onChange } }) => {
        const fileList = Array.isArray(value) ? value : [];
        return (
          <div className="position-relative">
            <input
              ref={healthCertificateInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              className="d-none"
              onChange={(e) => {
                const newFiles = e.target.files ? Array.from(e.target.files) : [];
                const valid = newFiles.filter(isAllowedFile);
                const invalidCount = newFiles.length - valid.length;
                setAttachmentError(
                  invalidCount > 0 ? t("ATTACHMENTS_PDF_JPG_PNG_ONLY") : ""
                );
                onChange([...fileList, ...valid]);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => healthCertificateInputRef.current?.click()}
            >
              <HugeiconsIcon icon={AttachmentIcon} size={18} />
              <span>{t("UPLOAD_HEALTH_CERTIFICATE")}</span>
            </button>
            {attachmentError && (
              <small className="d-block mt-1 text-danger">{attachmentError}</small>
            )}
            {fileList.length > 0 && !attachmentError && (
              <ul className="list-unstyled mt-2 mb-0" style={{ maxWidth: "300px" }}>
                {fileList.map((f, index) => (
                  <li
                    key={`${f.name}-${index}`}
                    className="d-flex align-items-center justify-content-between gap-2 py-1"
                  >
                    <small
                      className="text-muted flex-grow-1 text-truncate"
                      title={f.name}
                    >
                      {f.name}
                    </small>
                    <button
                      type="button"
                      className="btn text-danger p-0"
                      onClick={() => {
                        onChange(fileList.filter((_, i) => i !== index));
                      }}
                      title={t("DELETE")}
                      aria-label={t("DELETE")}
                    >
                      <HugeiconsIcon icon={DeleteIcon} size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {existingDocuments.length > 0 && (
              <div className="mt-3">
                <ul className="list-unstyled mb-0 p-0">
                  {existingDocuments.map((doc, index) => {
                    const { url, name } = getDocumentDisplay(doc);
                    const canDelete = Boolean(reportId);
                    const isThisDeleting = deletingIndex === index;
                    return (
                      <li
                        key={typeof doc === "string" ? doc : doc?.id ?? index}
                        className="d-flex align-items-center gap-2 py-1"
                      >
                        {isThisDeleting ? (
                          <Spinner size="sm" className="flex-shrink-0 me-1" />
                        ) : (
                          <HugeiconsIcon icon={AttachmentIcon} size={16} className="text-muted flex-shrink-0" />
                        )}
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-truncate flex-grow-1 min-w-0"
                            title={name}
                          >
                            {name}
                          </a>
                        ) : (
                          <small className="text-muted text-truncate flex-grow-1 min-w-0" title={name}>
                            {name}
                          </small>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="btn text-danger p-0 flex-shrink-0 d-flex align-items-center justify-content-center"
                            style={{ width: 24, height: 24 }}
                            onClick={() => {
                              setDeletingIndex(index);
                              deleteAttachment.mutate(
                                { reportId, attachmentIndex: index },
                                { onSettled: () => setDeletingIndex(null) }
                              );
                            }}
                            disabled={deletingIndex != null}
                            title={t("DELETE")}
                            aria-label={t("DELETE")}
                          >
                            {isThisDeleting ? (
                              <Spinner size="sm" />
                            ) : (
                              <HugeiconsIcon icon={DeleteIcon} size={16} />
                            )}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

export default DocumentField;
